import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import NaibLogo from '../components/NaibLogo';
import { EnvelopeIcon, KeyIcon } from '@heroicons/react/24/outline';
import { appConfig } from '../config/appConfig';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [infoMessage, setInfoMessage] = useState(location.state?.message || '');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!email || !password) {
      setError('Please enter both email and password');
      setErrorCode('');
      return;
    }

    setError('');
    setErrorCode('');
    setInfoMessage('');
    setLoading(true);

    try {
      const productId = appConfig.productId;
      const response = await login(email, password, productId);
      
      // If login was successful, redirect to the original destination or dashboard
      if (response.success) {
        const from = location.state?.from || '/dashboard';
        navigate(from, { replace: true });
        return;
      }

      setErrorCode(response.code || '');
      setError(response.error || 'Unable to sign in right now. Please try again, or contact your organization administrator.');
    } catch (err) {
      setError('Unable to sign in right now. Please try again, or contact your organization administrator.');
      setErrorCode('');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  } 

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-6 sm:px-6 lg:px-6">
      <div className="max-w-md w-full  p-8 md:p-10 2xl:p-12 3xl:p-14 bg-white rounded-2xl shadow-xl">
        <div className="pb-3">
          <NaibLogo logoClassName="h-9 w-9" nameClassName="h-7 w-auto max-w-[180px]" alt="Onahiri" />
        </div>
        
        <div className="text-sm font-light text-gray-500 pb-8">
          Sign in to your account on Onahiri.
        </div>

        <div className="flex flex-col">
          <form onSubmit={handleLogin}>
          {infoMessage && (
            <div className="rounded-md bg-green-50 p-4 mb-4">
              <div className="text-sm text-green-700">{infoMessage}</div>
            </div>
          )}

          {error && (
            <div className="rounded-md bg-red-50 p-4 mb-4">
              <div className="text-sm text-red-700">{error}</div>
              {errorCode === 'EMAIL_NOT_VERIFIED' && (
                <div className="mt-2 text-sm">
                  <Link to="/resend-verification" className="font-medium text-red-800 hover:text-red-700">
                    Resend verification email
                  </Link>
                </div>
              )}
            </div>
          )}

          <div className="pb-2">
            <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900">
              Email
            </label>
            <div className="relative text-gray-400">
              <span className="absolute inset-y-0 left-0 flex items-center p-1 pl-3">
                <EnvelopeIcon className="h-5 w-5" />
              </span>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="pl-12 mb-2 bg-gray-50 text-gray-600 border focus:border-transparent border-gray-300 sm:text-sm rounded-lg ring ring-transparent focus:ring-1 focus:outline-none focus:ring-gray-400 block w-full p-2.5 py-3 px-4"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="pb-6">
            <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-900">
              Password
            </label>
            <div className="relative text-gray-400">
              <span className="absolute inset-y-0 left-0 flex items-center p-1 pl-3">
                <KeyIcon className="h-5 w-5" />
              </span>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="pl-12 mb-2 bg-gray-50 text-gray-600 border focus:border-transparent border-gray-300 sm:text-sm rounded-lg ring ring-transparent focus:ring-1 focus:outline-none focus:ring-gray-400 block w-full p-2.5 py-3 px-4"
                placeholder="••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-3 mb-6">
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 text-white bg-gray-900 hover:bg-gray-800 focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center transition-colors duration-200 ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
            <Link
              to={`/onboarding/${appConfig.productId}`}
              className="flex-1 text-gray-900 bg-white border border-gray-300 hover:bg-gray-50 focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center transition-colors duration-200"
            >
              Sign up
            </Link>
          </div>

          <div className="text-sm font-light text-gray-500">
            <Link
              to="/forgot-password"
              className="font-medium text-gray-900 hover:text-gray-700"
            >
              Forgot your password?
            </Link>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;
